// websocket/environmental-payload-truth.ts — the page half of a destruction payload's pre-draw read (Lane A A17 / A18; session 4 R1b-2f).
//
// What the payload chain (inventory-destroy-truth.ts → janus-engine/inventory/payload-chain.ts) needs on the page BEFORE it resolves: every
// definition a payload names (the abilities, their statuses / damage types, the roster's templates and compositions) in the object cache,
// and every live creature member's full compile (the ability lane's rule). The chain is drawn on the page and saved lazily by
// options/game/actions/destruction_chain_save_inc.php — the per-step commit this file used to post to is retired (R-105).
import { fetchAndCacheObject } from './object-cache.js';
import { fetchAndCacheCaster } from './caster-cache.js';
import { actorEncounterFor, actorRowFor } from './movement-truth.js';
import { payloadDefinitionIds } from '../janus-engine/inventory/destruction-payload.js';

type Row = Record<string, unknown>;
const row = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** Every definition the step can read: the ability, what it names (statuses, damage types), the roster objects' templates and their
 * compositions. Fetched into the object cache so the resolve never meets an unheld definition mid-run. */
export async function prefetchPayloadDefinitions(encounter: Row, abilityIds: string | readonly string[]): Promise<void> {
  for (const abilityId of typeof abilityIds === 'string' ? [abilityIds] : abilityIds) await prefetch(encounter, upper(abilityId));
}
async function prefetch(encounter: Row, abilityId: string): Promise<void> {
  const ids = new Set<string>([abilityId]);
  const cache = (window.FateTide as unknown as { objectCache?: Map<string, unknown> } | undefined)?.objectCache;
  // a held definition is never re-fetched (fetchAndCacheObject always goes to the network): a warmed click makes no round trip
  if (!cache?.get(abilityId)) await fetchAndCacheObject(abilityId).catch(() => null);
  for (const id of payloadDefinitionIds(row(cache?.get(abilityId)))) ids.add(id);
  for (const inv of Array.isArray(encounter.encounter_inventories_arr) ? encounter.encounter_inventories_arr : []) {
    const itemId = upper(row(row(inv)?.inventory)?.inventory_item_id);
    if (UUID.test(itemId)) ids.add(itemId);
  }
  await Promise.all([...ids].map(id => cache?.get(id) ? null : fetchAndCacheObject(id).catch(() => null)));
  // second level: the roster objects' compositions
  const second = new Set<string>();
  for (const id of ids) for (const entry of ftLayerEntries?.(row(cache?.get(id)) ?? {}, 'item_composition') ?? []) {
    const cid = upper(typeof entry === 'string' ? entry : row(row(entry)?.items)?.item_id ?? row(entry)?.item_id);
    if (UUID.test(cid) && !cache?.get(cid)) second.add(cid);
  }
  await Promise.all([...second].map(id => fetchAndCacheObject(id).catch(() => null)));
}

/** Certify every live creature member's full compile (the ability lane's rule — a damage-taken reaction's carriers, the dead flag) and return
 * the certified set plus the refreshed encounter and member map; null = a member could not be certified (the caller hands back). */
const hydrating = new Map<string, Promise<void>>();
export async function certifyMembers(encounterId: string): Promise<{ encounter: Row; members: Map<string, Row>; complete: Set<string> } | null> {
  let encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return null;
  const ids = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])
    .map(member => upper(row(row(member)?.encounter_creatures)?.encounter_creature_id)).filter(id => UUID.test(id));
  const complete = new Set<string>();
  const full = (id: string): boolean => { const target = actorRowFor(encounterId, id) as Row | null; return !!row(row(target?.computed)?.creature) && !!row(target?.item_layer_types); };
  // Only the members this page does not already hold as a full compile are fetched (the caster cache follows every push): a click never
  // waits on a round trip for a row it has (R1b-2f — the chain's pre-draw read, owner 2026-09-29 "front end first").
  const hydrate = fetchAndCacheCaster as unknown as (targetId: string, options: { skipEncounterSeed: true; throwOnError: true }) => Promise<void>;
  // one hydration per member in flight: a click that lands while the radial's prewarm is still fetching joins it
  const join = (id: string): Promise<void> => {
    let pending = hydrating.get(id);
    if (!pending) { pending = hydrate(id, { skipEncounterSeed: true, throwOnError: true }).finally(() => hydrating.delete(id)); hydrating.set(id, pending); }
    return pending;
  };
  try { await Promise.all(ids.filter(id => !full(id)).map(join)); } catch { return null; }
  for (const id of ids) {
    if (!full(id)) return null;
    complete.add(id);
  }
  encounter = actorEncounterFor(encounterId) as Row | null;
  if (!encounter) return null;
  const members = new Map<string, Row>();
  for (const member of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const id = upper(row(row(member)?.encounter_creatures)?.encounter_creature_id);
    if (UUID.test(id)) members.set(id, member as Row);
  }
  return { encounter, members, complete };
}
Object.assign(window, { ftPrefetchPayloadDefinitions: prefetchPayloadDefinitions });
